import PropTypes from 'prop-types';
import React from 'react';

import t from '../../../labels/labelling-utils';
import { MatchStatusList, MatchStatusType } from '../../../typings/match';
import { IReportsDetailsMatch, MatchReportStatusList, ReportStatus } from '../../../typings/match-reports';
import { IPlayer, IReferee } from '../../../typings/player';
import {
  PanelVariantType,
  PanelVariantTypeList,
  PREDICTEDLINEUP,
  REPORTS,
  PREDICTEDLINEUPSLIM,
} from '../../../typings/report-tabs';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import MatchFormationPlayer from '../match-formation-player/match-formation-player';
import ReportPlayer from '../report-player/report-player';
import Team, { ITeam } from '../team/team';
import Text from '../../atoms/text/text';

import './report-tabs-panel.scss';

const formatPlayerWithJerseyNum = (player: IPlayer): string | null | undefined => {
  if (!player) {
    return null;
  }

  const { fullname, jerseyNum } = player;
  if (!jerseyNum) {
    return fullname;
  }

  return `${jerseyNum}. ${fullname}`;
};

export interface IReportTabsPanel extends Omit<IReportsDetailsMatch, 'home' | 'away' | 'referees' | 'status'> {
  away?: ITeam;
  home?: ITeam;
  panelVariant?: PanelVariantType;
  referees?: Array<IReferee>;
  status?: MatchStatusType;
}
interface IGroupList {
  groupName: string;
  home: Array<IPlayer>;
  away: Array<IPlayer>;
  referees?: Array<IReferee>;
  isArrayJoined: boolean;
  isMain?: boolean;
}

const classNames = (
  isHomeOrAwayEmpty: boolean,
  isHomeAndAway: boolean,
  away: ITeam | undefined,
  home: ITeam | undefined
): IClassNames =>
  makeClassNames({
    reportsPanel: 'report-tabs-panel',
    header: 'report-tabs-panel__header',
    body: 'report-tabs-panel__body',
    td: 'report-tabs-panel__header-td',
    col: ['report-tabs-panel__col', isHomeOrAwayEmpty && 'report-tabs-panel__col--full'],
    tdHomeFull: ['report-tabs-panel__td', !away && 'report-tabs-panel__td--full'],
    tdJoinedAway: [
      'report-tabs-panel__td',
      'report-tabs-panel__joined-array',
      isHomeAndAway && 'report-tabs-panel__td--right',
      !home && 'report-tabs-panel__td--full',
    ],
    tdJoinedHome: [
      'report-tabs-panel__td',
      'report-tabs-panel__joined-array',
      isHomeAndAway && 'report-tabs-panel__td--right',
      !away && 'report-tabs-panel__td--full',
    ],
    tdAwayFull: [
      'report-tabs-panel__td',
      // isHomeAndAway && 'report-tabs-panel__td--right', -> AWAY/RIGHT column should be more "left aligned" towards the center of the page
      !home && 'report-tabs-panel__td--full',
    ],
    colFull: 'report-tabs-panel__col report-tabs-panel__col--full',
    tdFull: ['report-tabs-panel__td', 'report-tabs-panel__td--full'],
    placeholder: 'report-tabs-panel__placeholder',
  });

const ReportTabsPanel: React.FC<IReportTabsPanel> = ({
  home,
  away,
  /* istanbul ignore next: default value */ referees = [],
  /* istanbul ignore next: default value */ panelVariant = REPORTS,
}) => {
  const groupList: Array<IGroupList> = [
    {
      groupName: t('report.tabs.panel.starting'),
      home: home?.playerList?.startingLineup || [],
      away: away?.playerList?.startingLineup || [],
      isArrayJoined: false,
      isMain: true,
    },
    {
      groupName:
        panelVariant === REPORTS ? t('report.tabs.panel.substitution') : t('formation.tabs.panel.substitution'),
      home: home?.playerList?.substitutes || [],
      away: away?.playerList?.substitutes || [],
      isArrayJoined: [PREDICTEDLINEUP, PREDICTEDLINEUPSLIM].includes(panelVariant),
    },
    {
      groupName: t('report.tabs.panel.coach'),
      home: home?.playerList?.manager ? [home.playerList.manager] : [],
      away: away?.playerList?.manager ? [away.playerList.manager] : [],
      isArrayJoined: [PREDICTEDLINEUP, PREDICTEDLINEUPSLIM].includes(panelVariant),
      isMain: true,
    },
    {
      groupName: panelVariant === REPORTS ? t('report.tabs.panel.referees') : t('formation.tabs.panel.referees'),
      home: [],
      away: [],
      isArrayJoined: false,
      referees,
    },
  ];

  const predictedLineupAddons: Array<IGroupList> = [
    {
      groupName: t('report.tabs.panel.potential.players'),
      home: home?.playerList?.potentialPlayers ?? [],
      away: away?.playerList?.potentialPlayers ?? [],
      isArrayJoined: true,
    },
    {
      groupName: t('report.tabs.panel.disqualifieds'),
      home: home?.playerList?.disqualifieds ?? [],
      away: away?.playerList?.disqualifieds ?? [],
      isArrayJoined: true,
    },
    {
      groupName: t('report.tabs.panel.unavailables'),
      home: home?.playerList?.unavailables ?? [],
      away: away?.playerList?.unavailables ?? [],
      isArrayJoined: true,
    },
  ];

  const groupListWithoutCoach: Array<IGroupList> = groupList.slice(0, 2).concat(groupList[3]);

  const predictedLineupGroupList: Array<IGroupList> = groupListWithoutCoach
    .concat(predictedLineupAddons, groupList[2])
    .filter((item) => {
      const refObj = groupList.find((obj: IGroupList) => obj.referees);
      return item !== refObj;
    });

  const predictedLineupSlimList: Array<IGroupList> = predictedLineupGroupList.filter((item) => {
    return item.groupName !== t('report.tabs.panel.starting');
  });

  const isHomeAndAway = Boolean(home && away);
  const isHomeOrAwayEmpty = Boolean(!home || !away);

  const cn = classNames(isHomeOrAwayEmpty, isHomeAndAway, away, home);

  const placeholder = <div className={cn.placeholder} />;

  const getGroupList = () => {
    switch (panelVariant) {
      case PREDICTEDLINEUP:
        return predictedLineupGroupList;
      case PREDICTEDLINEUPSLIM:
        return predictedLineupSlimList;
      default:
        return groupList;
    }
  };

  const shouldRenderNotMainGroupAsMain = (isMain?: boolean) =>
    [PREDICTEDLINEUP, PREDICTEDLINEUPSLIM].includes(panelVariant) ? Boolean(isMain) : true;

  return (
    <table className={cn.reportsPanel} data-panels>
      <tbody className={cn.body}>
        {/* FIXME a lots of any types  and key={index} */}
        {getGroupList().map((group, i: number) => {
          let maxLength = 1;
          let homeTeam: any[] = [];
          let awayTeam: any[] = [];
          if (group.home && group.away) {
            // Tests reported a NaN array length, so...
            const homeLength = group.home.length || 0;
            const awayLength = group.away.length || 0;
            maxLength = Math.max(homeLength, awayLength) || 0;
            homeTeam = [...group.home, ...Array.from(Array(maxLength - homeLength))];
            awayTeam = [...group.away, ...Array.from(Array(maxLength - awayLength))];
          }

          const joinHomeTeamArray = homeTeam.map(formatPlayerWithJerseyNum).filter(Boolean).join(', ') || '-';

          const joinAwayTeamArray = awayTeam.map(formatPlayerWithJerseyNum).filter(Boolean).join(', ') || '-';

          return (
            <React.Fragment key={i}>
              {(homeTeam.length + awayTeam.length > 0 || group.referees) && (
                <tr className={cn.header}>
                  <td colSpan={maxLength} className={cn.td}>
                    {group.groupName}
                  </td>
                </tr>
              )}
              {home && (
                <tr className={cn.col} data-panel-left>
                  {!group.isArrayJoined &&
                    homeTeam.map((player: IPlayer | undefined, teamIndex) => (
                      <td key={teamIndex} className={cn.tdHomeFull}>
                        {player && home ? (
                          <React.Fragment>
                            {panelVariant === REPORTS ? (
                              <ReportPlayer {...player} />
                            ) : (
                              <React.Fragment>
                                {shouldRenderNotMainGroupAsMain(group.isMain) && (
                                  <MatchFormationPlayer
                                    {...player}
                                    goals={home.goals}
                                    scorersCards={home.scorersCards}
                                    substitution={home.substitution}
                                    topPlayerId={home.topPlayerId}
                                  />
                                )}
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        ) : (
                          placeholder
                        )}
                      </td>
                    ))}
                  {group.isArrayJoined && home && (
                    <td className={cn.tdJoinedHome}>
                      <Text fontSize="16--14" color="dark">
                        {joinHomeTeamArray}
                      </Text>
                    </td>
                  )}
                </tr>
              )}
              {away && (
                <tr className={cn.col} data-panel-right>
                  {!group.isArrayJoined &&
                    awayTeam.map((player: IPlayer | undefined, teamIndex) => (
                      <td key={teamIndex} className={cn.tdAwayFull}>
                        {player && away ? (
                          <React.Fragment>
                            {panelVariant === REPORTS ? (
                              <ReportPlayer {...player} />
                            ) : (
                              <React.Fragment>
                                {shouldRenderNotMainGroupAsMain(group.isMain) && (
                                  <MatchFormationPlayer
                                    {...player}
                                    goals={away.goals}
                                    scorersCards={away.scorersCards}
                                    substitution={away.substitution}
                                    topPlayerId={away.topPlayerId}
                                  />
                                )}
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        ) : (
                          placeholder
                        )}
                      </td>
                    ))}
                  {group.isArrayJoined && away && (
                    <td className={cn.tdJoinedAway}>
                      <Text fontSize="16--14" color="dark">
                        {joinAwayTeamArray}
                      </Text>
                    </td>
                  )}
                </tr>
              )}
              {group.referees && group.referees.length > 0 && (
                <tr className={cn.colFull}>
                  {group.referees.map((player: IPlayer, j: number) => (
                    <td key={j} className={cn.tdFull}>
                      {<ReportPlayer {...player} />}
                    </td>
                  ))}
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  ReportTabsPanel.propTypes = {
    away: PropTypes.exact({
      ...Team.propTypes,
    }),
    home: PropTypes.exact({
      ...Team.propTypes,
    }),
    panelVariant: PropTypes.oneOf(PanelVariantTypeList),
    referees: PropTypes.arrayOf(
      PropTypes.exact({
        fullname: isPropTypeNullable(PropTypes.string.isRequired),
        id: isPropTypeNullable(PropTypes.number.isRequired),
        name: isPropTypeNullable(PropTypes.string.isRequired),
        surname: isPropTypeNullable(PropTypes.string.isRequired),
      }).isRequired
    ),
    reportStatus: isPropTypeNullable(PropTypes.oneOf<ReportStatus>(MatchReportStatusList)),
    reportUrl: isPropTypeNullable(PropTypes.string),
    status: PropTypes.oneOf<MatchStatusType>(MatchStatusList),
  };
}

export default ReportTabsPanel;
